import React, {useEffect, useLayoutEffect, useRef} from 'react';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import {FOLLOW_UP_CHOICES, FollowUpRow} from './follow-ups-form';
import './FollowUpKit.css';

/*
 * The follow-up settings pieces, ported from Android's followups/
 * FollowUpsScreen.kt (SaveRow, Note, ErrorBox, SwitchRow, FollowUpCard,
 * DayPill, DayPicker, MethodBox, AddButton) for SQ-687 / SQ-688's invoice
 * page. Sizes are Android's dp taken as CSS px; colours are the theme's (see
 * FollowUpKit.css). Everything sits inside an element with the `lp fu`
 * classes, which carry the colour variables.
 */

export const TEXT_NOT_READY = "Text follow-ups aren't set up yet.";
/** How long the Text note stays after a tap. */
export const TEXT_NOTE_MS = 4000;

export const Note = ({text}: {text: string}) => <p className="fu-note">{text}</p>;

/** White with a red edge rather than a red fill, so no new colour is needed. */
export const ErrorBox = ({text}: {text: string}) => <div className="fu-error-box" role="alert">{text}</div>;

/**
 * Right-aligned, so the button stays anchored at the right edge whether or
 * not the readout is there. The button never changes its look.
 */
export const SaveRow = ({dirty, justSaved, saving, onSave}: {dirty: boolean; justSaved: boolean; saving?: boolean; onSave: () => void}) => (
  <div className="fu-save-row">
    {(dirty || justSaved) && (
      <span className={dirty ? 'fu-save-state fu-save-state--dirty' : 'fu-save-state'} role="status">
        {dirty ? 'Unsaved' : 'Saved'}
      </span>
    )}
    <button type="button" className="fu-save" disabled={saving} onClick={onSave}>Save changes</button>
  </div>
);

/** One switch for the whole feature: the row is the tap target. */
export const SwitchRow = ({on, onToggle, title, onLine}: {on: boolean; onToggle: () => void; title: string; onLine: string}) => (
  <button type="button" role="switch" aria-checked={on} className={on ? 'fu-switch-row fu-switch-row--on' : 'fu-switch-row'} onClick={onToggle}>
    <span className="fu-switch-text">
      <span className="fu-switch-title">{title}</span>
      <span className="fu-switch-sub">{on ? onLine : 'Off'}</span>
    </span>
    <span className="fu-switch-track" aria-hidden="true"><span className="fu-switch-thumb"/></span>
  </button>
);

const ChevronDown = () => (
  <svg className="fu-chevron" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 8L12 16L20 8" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/** The pill that opens a day list; `placeholder` greys a choice not made yet. */
export const DayPill = ({label, on, wrong, onClick, placeholder, open}: {
  label: string; on: boolean; wrong: boolean; onClick: () => void; placeholder?: boolean; open?: boolean;
}) => (
  <button type="button" className={['fu-day-pill', wrong && 'fu-day-pill--wrong', (!on || placeholder) && 'fu-day-pill--faint'].filter(Boolean).join(' ')}
          disabled={!on} aria-expanded={on ? !!open : undefined} onClick={onClick}>
    {label}
    {/* Switched off there is nothing to open, so no chevron. */}
    {on && <ChevronDown/>}
  </button>
);

/**
 * The day list, drawn in the card rather than a system dropdown: white rows,
 * the app's radio, no tinted row. Opens scrolled to the current choice, a
 * couple of rows above it so it is not pinned to the top edge.
 */
export const DayPicker = ({choices, selected, label, onPick}: {
  choices: number[]; selected: number; label: (d: number) => string; onPick: (d: number) => void;
}) => {
  const list = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = list.current;
    const first = element?.children[Math.max(0, choices.indexOf(selected) - 2)] as HTMLElement | undefined;
    if (element && first) element.scrollTop = first.offsetTop;
    // Only on opening: picking closes the list anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="fu-day-picker" role="radiogroup" ref={list}>
      {choices.map(d => {
        const sel = d === selected;
        return (
          <button key={d} type="button" role="radio" aria-checked={sel} className={sel ? 'fu-day-row fu-day-row--selected' : 'fu-day-row'}
                  onClick={() => onPick(d)}>
            <span className="fu-radio" aria-hidden="true"/>
            {label(d)}
          </button>
        );
      })}
    </div>
  );
};

const MethodBox = ({label, checked, on, wrong, locked, onClick}: {
  label: string; checked: boolean; on: boolean; wrong: boolean; locked: boolean; onClick: () => void;
}) => (
  <button type="button" role="checkbox" aria-checked={locked ? false : checked} disabled={!on}
          aria-label={locked ? `${label} (not available yet)` : undefined}
          className={['fu-method', checked && 'fu-method--checked', !on && 'fu-method--off', wrong && 'fu-method--wrong', locked && 'fu-method--locked']
            .filter(Boolean).join(' ')}
          onClick={onClick}>
    <span className="fu-method-box" aria-hidden="true">
      {checked ? <CheckIcon/> : locked ? <LockIcon/> : null}
    </span>
    {label}
  </button>
);

/** "7 days after", "1 day after", and "0 days" for the unset one (the estimate page's wording). */
const sentDayLabel = (d: number) => (d === 0 ? '0 days' : `${d} ${d === 1 ? 'day' : 'days'} after`);

export const FollowUpCard = ({
  index, row, on, dayWrong, methodWrong, messages, textTap, onTextNoteDone, pickerOpen, onTogglePicker, onPick,
  onToggleEmail, onTapText, onDelete, dayLabel = sentDayLabel,
}: {
  index: number; row: FollowUpRow; on: boolean; dayWrong: boolean; methodWrong: boolean; messages: string[];
  /** When the locked Text box was last tapped, so the card says why for a few seconds. */
  textTap?: number; onTextNoteDone: (tap: number) => void;
  pickerOpen: boolean; onTogglePicker: () => void; onPick: (d: number) => void;
  onToggleEmail: () => void; onTapText: () => void; onDelete: () => void;
  /** The day pill's wording; the invoice page counts from the due date. */
  dayLabel?: (d: number) => string;
}) => {
  useEffect(() => {
    if (textTap === undefined) return undefined;
    const timer = window.setTimeout(() => onTextNoteDone(textTap), TEXT_NOTE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textTap]);
  // A plain settings card, no list-card accent: these are edited in place,
  // not opened, and the outline carries the state.
  const className = ['fu-card', !on && 'fu-card--off', on && (dayWrong || methodWrong) && 'fu-card--wrong'].filter(Boolean).join(' ');
  return (
    <div className={className} role="group" aria-label={`Follow-up ${index + 1}`}>
      <div className="fu-card-head">
        <span className="fu-card-label">Follow up</span>
        <DayPill label={dayLabel(row.days)} placeholder={row.days === 0} on={on} wrong={dayWrong} open={pickerOpen} onClick={onTogglePicker}/>
        <span className="fu-spacer"/>
        {on && (
          // The app's delete mark (work items, operations): the red trash can.
          <button type="button" className="fu-delete" aria-label={`Delete follow-up ${index + 1}`} onClick={onDelete}>
            <DeleteIcon/>
          </button>
        )}
      </div>

      {pickerOpen && <DayPicker choices={FOLLOW_UP_CHOICES} selected={row.days} label={dayLabel} onPick={onPick}/>}

      {/* Its own line, so Call can join these later. */}
      <div className="fu-methods">
        {/* Text shows where it will live but cannot be picked until the server can send follow-up texts; a tap says so. */}
        <MethodBox label="Text" checked={false} on={on} wrong={false} locked onClick={() => on && onTapText()}/>
        <MethodBox label="Email" checked={row.email} on={on} wrong={methodWrong} locked={false} onClick={() => on && onToggleEmail()}/>
      </div>

      {on && textTap !== undefined && <div className="fu-card-note" role="status">{TEXT_NOT_READY}</div>}
      {messages.length > 0 && (
        <div className="fu-card-messages">
          {messages.map(m => <div key={m}>{m}</div>)}
        </div>
      )}
    </div>
  );
};

/** Full-width green-outlined button under the cards. */
export const AddButton = ({text, onClick}: {text: string; onClick: () => void}) => (
  <button type="button" className="fu-add" onClick={onClick}>{text}</button>
);
